import { CartRule } from 'cart-rule';
class CartRuleEngine {
	constructor({
		rules = [],
		options = {},
		successMessage,
		displayMode = 'progress_bar',
		textJoiner = ' und ',
		showAllTexts = false,
	}) {
		const defaultOptions = {
			removeUnqualifiedGift: true,
		};
		this.options = {
			...defaultOptions,
			...options,
		};
		this.successMessage = successMessage;
		this.textJoiner = textJoiner;
		this.showAllTexts = showAllTexts;
		this.rules = rules.map((rule) => new CartRule(rule));
		this.baseDisplayMode = displayMode;
		this.initRulePositions();
	}
	initRulePositions() {
		this.hasQuantityRule = this.rules.some((rule) => rule.ruleType === 'CART_BAR_ITEM_QUANTITY' && rule.isActive);
		this.displayMode = this.hasQuantityRule ? 'milestones' : this.baseDisplayMode;
		const activeRules = this.rules.filter((rule) => rule.isActive);
		if (this.displayMode === 'milestones') {
			activeRules.forEach((rule, index) => {
				rule.position = (index + 1) / (activeRules.length + 1);
			});
			return;
		}
		activeRules
			.sort((a, b) => {
				return a.threshold - b.threshold;
			})
			.forEach((rule, index) => {
				rule.position = (index + 1) / (activeRules.length + 1);
			});
	}
	getItemCountForRule(cart, rule) {
		// exclude auto-added gift line items from the count
		const giftVariantIDs = new Set(this.rules.map((r) => r.getGiftVariantID()).filter(Boolean));
		return cart.items.reduce((count, item) => {
			if (giftVariantIDs.has(item.variant_id)) return count;
			if (rule.eligibleProductIDs && !rule.eligibleProductIDs.includes(item.product_id)) return count;
			return count + item.quantity;
		}, 0);
	}
	// threshold isn't comparable across mixed rule types, so only sort by it
	// in progress-bar mode (money-only); keep list order otherwise.
	sortRulesForMode(rules) {
		if (this.displayMode === 'milestones') return rules;
		return rules.sort((a, b) => a.threshold - b.threshold);
	}
	getQualifiedRules(cart) {
		const discountedPricePerOrder = cart.cart_level_discount_applications.reduce((total, app) => {
			return total + (app.total_allocated_amount || 0);
		}, 0);
		const qualified = this.rules.filter((rule) =>
			rule.isRuleQualified({
				cartTotal: cart.total_price,
				discountedPricePerOrder,
				itemCount: this.getItemCountForRule(cart, rule),
			}),
		);
		return this.sortRulesForMode(qualified);
	}

	getUnqualifiedRules(cart) {
		const discountedPricePerOrder = cart.cart_level_discount_applications.reduce((total, app) => {
			return total + (app.total_allocated_amount || 0);
		}, 0);
		const unqualified = this.rules
			.filter((rule) => rule.isActive)
			.filter(
				(rule) =>
					!rule.isRuleQualified({
						cartTotal: cart.total_price,
						discountedPricePerOrder,
						itemCount: this.getItemCountForRule(cart, rule),
					}),
		);
		return this.sortRulesForMode(unqualified);
	}
	getShippingPrice(cart) {
		const shippingRule = this.rules.find((rule) => rule.ruleType === 'CART_BAR_ITEM_SHIPPING');
		if (shippingRule) {
			return shippingRule.getShippingPrice({ cartTotal: cart.total_price });
		}
		return null;
	}
	getFormattedText(cart) {
		const unqualifiedRules = this.getUnqualifiedRules(cart);
		if (unqualifiedRules.length > 0) {
			if (this.displayMode === 'milestones' && this.showAllTexts) {
				const texts = unqualifiedRules.map((rule) => {
					const itemCount = this.getItemCountForRule(cart, rule);
					return rule.getFormattedRemainingText({ cartTotal: cart.total_price, itemCount });
				});
				return texts.join(this.textJoiner);
			}
			const nextRule = unqualifiedRules[0];
			const itemCount = this.getItemCountForRule(cart, nextRule);
			return nextRule.getFormattedRemainingText({ cartTotal: cart.total_price, itemCount });
		}
		return this.successMessage;
	}
	getPayloadsRules(cart) {
		return this.options.removeUnqualifiedGift ? this.rules : this.getQualifiedRules(cart);
	}
	processCartRules(cart) {
		const removedItemIds = this.getRemovedItemIDs(cart);
		const qualifiedRules = this.getQualifiedRules(cart);
		const payloads = this.getPayloadsRules(cart)
			.filter((rule) => !rule.isRuleGiftRemoved(removedItemIds))
			.map((rule) =>
				rule.getUpdatePayload({
					cartTotal: cart.total_price,
					itemCount: this.getItemCountForRule(cart, rule),
					lineItems: cart.items,
				}),
			)
			.filter((payload) => payload !== null);
		const result = {
			qualifiedRules,
			payloads,
			formattedText: this.getFormattedText(cart),
			shippingPrice: this.getShippingPrice(cart),
		};
		if (this.displayMode !== 'milestones') {
			result.cartBarProgress = this.getCartBarProgress(cart);
		}
		return result;
	}
	getCartBarProgress(cart) {
		const totalPrice = cart.total_price;
		const qualifiedRules = this.getQualifiedRules(cart);
		const unqualifiedRules = this.getUnqualifiedRules(cart);
		const startRule = qualifiedRules.pop();
		const endRule = unqualifiedRules.shift();
		if (!endRule) {
			// all rules are qualified
			return 100;
		}
		const startThreshold = startRule?.threshold || 0;
		const endThreshold = endRule?.threshold || 100;
		const startPosition = startRule?.position * 100 || 0;
		const endPosition = endRule?.position * 100 || 100;
		const progressBetweenBothPositions = (totalPrice - startThreshold) / (endThreshold - startThreshold);
		const progress = startPosition + progressBetweenBothPositions * (endPosition - startPosition);
		return progress;
	}
	getRulePositions() {
		return this.rules
			.filter((rule) => rule.isActive)
			.map((rule) => ({
				ruleID: rule.ruleID,
				position: rule.position,
			}));
	}
	getRemovedItemIDs(cart) {
		return cart?.items_removed?.map((item) => item.variant_id) || [];
	}
}
export { CartRuleEngine };
